import PoliceLineDivider from '@/components/police-line-divider';
import SearchSelect from '@/components/search-select';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  PRIVACY_POLICY_DATA,
  REGISTER_TEXT_FORM_DATA,
} from '@/data/franchise/data';
import { useFranchiseReserve } from '@/services/mutation/franchise.mutation';
import {
  AgeGroupType,
  formSchema,
  FormValues,
} from '@/services/types/franchise.type';
import { FormElementType, LabelValuePair } from '@/services/types/globals.type';
import { zodResolver } from '@hookform/resolvers/zod';
import { Checkbox } from '@radix-ui/react-checkbox';
import { CheckIcon } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

export default function RegisterSection() {
  const [showTerms, setShowTerms] = useState(false);
  // const isMobile = useMobile();

  // 나이대
  const ageGroups: LabelValuePair[] = [
    { value: AgeGroupType.AGE_20, label: AgeGroupType.AGE_20 + '대' },
    { value: AgeGroupType.AGE_30, label: AgeGroupType.AGE_30 + '대' },
    { value: AgeGroupType.AGE_40, label: AgeGroupType.AGE_40 + '대' },
    { value: AgeGroupType.AGE_50, label: AgeGroupType.AGE_50 + '대' },
    { value: AgeGroupType.AGE_MORE, label: '60대 이상' },
  ];
  const [ageFilter, setAgeFilter] = useState<LabelValuePair>();

  const defaultValues = {
    realname: '',
    ageGroup: undefined,
    phoneNumber: '',
    email: '',
    regionTxt: '',
    budget: '',
    content: '',
    privacyAgreement: false,
  };
  // Initialize the form with React Hook Form and Zod resolver
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultValues,
    mode: 'onChange', // Validate on change to enable/disable submit button
  });

  // Get form state to check validity
  const { isSubmitting, dirtyFields } = form.formState;

  // Check if all required fields are filled
  const allFieldsFilled =
    dirtyFields.realname &&
    dirtyFields.ageGroup &&
    dirtyFields.phoneNumber &&
    dirtyFields.email &&
    dirtyFields.regionTxt &&
    dirtyFields.budget &&
    dirtyFields.content &&
    form.getValues('privacyAgreement');

  const reserve = useFranchiseReserve();

  // Form submission handler
  const onSubmit = (data: FormValues) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { privacyAgreement, ...bodyData } = data;

    reserve.mutate(bodyData, {
      onSuccess: () => {
        toast('상담신청이 완료되었습니다.', {
          description: '빠른 시간안에 연락드리겠습니다.',
          duration: 3000,
          position: 'top-center',
          style: {
            color: '#ffffff',
          },
          className: 'text-white [&>div]:text-white [&>div>div]:text-white',
        });
        setAgeFilter(undefined);
        form.reset(defaultValues);
      },
      onError: () => {
        toast('상담신청을 실패하였습니다. 다시 시도해주세요.', {
          position: 'top-center',
          style: {
            color: '#ffffff',
          },
          className: 'text-white [&>div]:text-white [&>div>div]:text-white',
        });
      },
    });
  };

  return (
    <section className="desktop:web-w desktop:mx-auto">
      <PoliceLineDivider />

      <h2 className="typo-28 text-level-2 desktop:typo-50 desktop:mb-20 desktop:pt-30 pt-15 text-center font-black">
        온라인 상담신청
      </h2>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="desktop:pt-0 flex flex-col pt-10"
        >
          <div className="desktop:grid-cols-2 grid grid-cols-1 gap-6">
            {REGISTER_TEXT_FORM_DATA.filter(
              (item) => item.elementType !== FormElementType.Textarea,
            ).map((item) => {
              return (
                <FormField
                  key={item.name}
                  control={form.control}
                  name={item.name}
                  render={({ field }) => {
                    const label = (
                      <FormLabel>
                        <div className="typo-20 text-level-1 font-bold">
                          {item.label}{' '}
                          {item.isRequired && (
                            <span className="text-main-orange">(필수)</span>
                          )}
                        </div>
                      </FormLabel>
                    );

                    switch (item.elementType) {
                      case FormElementType.Text:
                        return (
                          <div>
                            <FormItem className="flex flex-col gap-4">
                              {label}
                              <FormControl>
                                <Input
                                  type="text"
                                  placeholder={item.placeholder}
                                  {...field}
                                  value={field.value as string}
                                  className="border-stroke-white placeholder-level-6 text-level-3 !typo-18 h-16.5 w-full rounded-xl bg-white px-6 py-5 font-semibold focus-visible:ring-1"
                                />
                              </FormControl>
                              <FormMessage className="typo-15 pt-1 font-semibold text-red-600" />
                            </FormItem>
                          </div>
                        );

                      case FormElementType.Select:
                        return (
                          <div>
                            <FormItem className="flex flex-col gap-4">
                              {label}
                              <FormControl>
                                <SearchSelect
                                  triggerPlaceholder={'점주 연령대 선택'}
                                  selected={ageFilter ? [ageFilter] : []}
                                  onFilterChange={(filter) => {
                                    field.onChange(filter.value);
                                    setAgeFilter(filter);
                                  }}
                                  list={ageGroups}
                                  triggerAddClass="!h-[66px] !rounded-xl !px-6"
                                  itemAddClass="px-6"
                                />
                              </FormControl>
                              <FormMessage className="typo-15 pt-1 font-semibold text-red-600" />
                            </FormItem>
                          </div>
                        );

                      default:
                        return <></>;
                    }
                  }}
                />
              );
            })}
          </div>

          {/* 텍스트에리어 (상담내용) - 그리드 밖에 별도 배치 */}
          <div className="mt-5">
            {REGISTER_TEXT_FORM_DATA.filter(
              (item) => item.elementType === FormElementType.Textarea,
            ).map((item) => (
              <FormField
                key={item.name}
                control={form.control}
                name={item.name}
                render={({ field }) => (
                  <div>
                    <FormItem className="flex flex-col gap-4">
                      <FormLabel>
                        <div className="typo-20 text-level-1 font-bold">
                          {item.label}{' '}
                          {item.isRequired && (
                            <span className="text-main-orange">(필수)</span>
                          )}
                        </div>
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder={item.placeholder}
                          {...field}
                          value={field.value as string}
                          className="border-stroke-white placeholder-level-6 text-level-3 !typo-18 desktop:h-[180px] h-[220px] w-full rounded-xl bg-white px-6 py-5 font-semibold focus-visible:ring-1"
                        ></Textarea>
                      </FormControl>
                      <FormMessage className="typo-15 pt-1 font-semibold text-red-600" />
                    </FormItem>
                  </div>
                )}
              />
            ))}
          </div>

          <div className="mt-10.5">
            <div className="desktop:items-center flex flex-col items-start">
              <FormField
                control={form.control}
                name="privacyAgreement"
                render={({ field }) => (
                  <>
                    <FormItem className="desktop:w-[380px] flex w-full flex-row items-center space-y-0 space-x-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          className="bg-stroke-white text-sub-white data-[state=checked]:bg-main-orange flex size-5 cursor-pointer items-center justify-center rounded-full"
                        >
                          <CheckIcon className="visible flex size-4" />
                        </Checkbox>
                      </FormControl>
                      <div className="flex w-full items-center justify-between">
                        <FormLabel>
                          <span className="text-level-1 typo-16 cursor-pointer font-semibold">
                            (필수) 개인정보 수집 이용에 동의합니다.
                          </span>
                        </FormLabel>
                        <Button
                          type="button"
                          className="m-0 cursor-pointer border-none bg-transparent p-0 shadow-none"
                          onClick={() => setShowTerms(!showTerms)}
                        >
                          <span className="typo-14 text-level-5 font-semibold underline">
                            보기
                          </span>
                        </Button>
                      </div>
                    </FormItem>
                    <FormMessage className="typo-15 font-semibold text-red-600" />
                  </>
                )}
              />
            </div>

            {/* Terms and conditions container - only visible when showTerms is true */}
            {showTerms && (
              <div className="border-stroke-white h-60 overflow-hidden rounded-md border bg-gray-50">
                <div className="h-full overflow-y-auto px-7 pt-10 pb-7">
                  <p className="typo-16 text-level-2 pb-2 font-bold">
                    개인정보 수집 및 이용 목적
                  </p>
                  <p className="typo-14 text-level-2 pb-10 leading-relaxed font-medium">
                    드라이빙존 운전면허는 고객님의 개인정보를 중요시하며,
                    "정보통신망 이용촉진 및 정보보호"에 관한 법률을 준수하고
                    있습니다.
                  </p>
                  {PRIVACY_POLICY_DATA.map((item) => (
                    <div key={item.idx}>
                      <p className="typo-14 text-level-2 pb-1 font-bold">
                        {item.idx}. {item.title}
                      </p>
                      <p className="typo-14 text-level-4 pb-4 leading-relaxed font-medium">
                        {item.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Submit button - fixed to bottom on mobile */}
            {/* <div className={`${isMobile ? '' : ''}`}> */}
            <div
              className={`${showTerms ? 'desktop:pt-10' : 'pt-3'} desktop:flex desktop:justify-center`}
            >
              <Button
                type="submit"
                className="bg-main-orange text-sub-white desktop:w-[380px] desktop:h-[64px] h-15 w-full cursor-pointer rounded-lg"
                disabled={!allFieldsFilled || isSubmitting}
              >
                <span className="typo-24 font-extrabold">
                  가맹문의 상담 신청
                </span>
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </section>
  );
}
